import { useEffect, useState } from "react";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectProps } from "@mui/material/Select";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

export interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

type MicrophoneFormFieldProps = {
  label: string;
  value: string;
  color?: string;
  onChange: (deviceId: string) => void;
  sx?: SxProps;
  menuProps?: SelectProps["MenuProps"];
};
export default function MicrophoneFormField({
  label,
  value,
  color,
  sx,
  onChange,
  menuProps
}: MicrophoneFormFieldProps) {
  const theme = useTheme();
  const [microphones, setMicrophones] = useState<MicrophoneDevice[]>([]);

  useEffect(() => {
    const fetchMicrophones = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphoneDevices = devices
          .filter((device) => device.kind === "audioinput")
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${devices.indexOf(device) + 1}`
          }));

        setMicrophones(microphoneDevices);

        const hasMatch = microphoneDevices.some((d) => d.deviceId === value);
        if (!hasMatch && microphoneDevices.length > 0) {
          onChange(microphoneDevices[0].deviceId);
        }
      } catch (error) {
        console.error("Error enumerating devices:", error);
      }
    };

    fetchMicrophones();
  }, [value, onChange]);

  return (
    <FormControl fullWidth sx={sx}>
      <InputLabel
        id="microphone-select-label"
        sx={{
          color,
          "&.Mui-focused": {
            color
          }
        }}
      >
        {label}
      </InputLabel>
      <Select
        labelId="microphone-select-label"
        value={microphones.length === 0 ? "" : value}
        onChange={(event) => onChange(event.target.value as string)}
        label={label}
        MenuProps={{
          style: { zIndex: theme.zIndex.snackbar + 2 },
          ...menuProps
        }}
      >
        {microphones.map((microphone) => (
          <MenuItem key={microphone.deviceId} value={microphone.deviceId}>
            {microphone.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
